import { useState, useEffect, useMemo, useCallback } from 'react'
import SearchBar from '@/components/common/SearchBar'
import { Box, Checkbox, Typography, CircularProgress } from '@mui/material'
import { useAppTheme } from '@/theme/ThemeContext'
import { getFilterOptions } from '@/services/consumerService'
import { useTableStore } from '@/store/table/table'
import { getTableFilterValue } from '@/utils/tableFilterValue'

interface OptionsFilterProps {
  field?: string
  checkBox?: boolean
  /** The list without its own frame and clear link, for a card that owns both. */
  bare?: boolean
  /** The choices are the values of the rows the grid holds. The Gateway is not asked. */
  rowsOnly?: boolean
  Icon?: any
  options?: { id: string | number; label: string }[]
  selected?: string[]
  onChange?: (values: string[]) => void
}

const WEEKDAYS_OPTIONS = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש']

const formatFilterLabel = (val: string, fieldName: string): string => {
  if (!val) return val
  if (fieldName === 'zoneType') {
    if (val === '1') return 'עץ'
    if (val === '2') return 'קבוצה'
  }
  if (fieldName === 'dayOfWeek' || fieldName === 'day') {
    const dayLabels: Record<string, string> = {
      'א': 'יום א׳ (ראשון)',
      'ב': 'יום ב׳ (שני)',
      'ג': 'יום ג׳ (שלישי)',
      'ד': 'יום ד׳ (רביעי)',
      'ה': 'יום ה׳ (חמישי)',
      'ו': 'יום ו׳ (שישי)',
      'ש': 'יום ש׳ (שבת)',
    }
    return dayLabels[val] ?? val
  }
  const isDateField =
    fieldName.toLowerCase().includes('date') ||
    fieldName.toLowerCase().includes('time') ||
    fieldName.toLowerCase().includes('at')
  const isISOString =
    typeof val === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(val)
  if (isDateField || isISOString) {
    const date = new Date(val)
    if (!isNaN(date.getTime())) {
      const hasTime =
        date.getHours() !== 0 ||
        date.getMinutes() !== 0 ||
        date.getSeconds() !== 0
      if (hasTime) {
        return date.toLocaleString('he-IL', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        })
      } else {
        return date.toLocaleDateString('he-IL', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        })
      }
    }
  }
  return val
}

const OptionsFilter = ({
  field,
  checkBox = true,
  bare = false,
  rowsOnly = false,
  Icon = null,
  options: customOptions,
  selected: externalSelected,
  onChange,
}: OptionsFilterProps) => {
  const {
    apiFilters,
    updateFieldFilter,
    rowsStore,
    searchFilterOptions,
    rememberSearchFilterOptions,
  } = useTableStore()
  // Get current filter values for this field from store
  const currentFieldFilter = apiFilters.find(f => f.name === field)?.value ?? []

  const [localChecked, setChecked] = useState<string[]>(
    externalSelected ?? currentFieldFilter
  )
  const checked = bare ? currentFieldFilter : localChecked
  const [searchValue, setSearchValue] = useState('')
  const [apiOptions, setApiOptions] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)

  const rowOptions = useMemo(() => {
    if (!field) return []
    const values = new Set<string>()
    rowsStore.forEach(row => {
      const value = getTableFilterValue(row, field)
      if (value != null && String(value).trim() && String(value) !== '-')
        values.add(String(value))
    })
    return Array.from(values).sort()
  }, [field, rowsStore])

  const { mode, langKey } = useAppTheme()
  const isDarkMode = mode === 'dark'

  const colors = {
    bg: isDarkMode ? '#202B40' : '#F8FAFD',
    hoverBg: isDarkMode ? '#1D325A' : '#EFF2F7',
    text: isDarkMode ? '#FFFFFF' : '#003F61',
    selectedBg: isDarkMode ? '#374151' : '#E3F5FF',
    scrollTrack: isDarkMode ? '#1D325A' : '#E4E7EB',
    scrollThumb: isDarkMode ? '#374151' : '#C1C7CD',
    scrollThumbHover: isDarkMode ? '#4B5563' : '#A0A6AD',
    hoverBorder: isDarkMode ? '#374151' : '#E4E7EB',
  }

  // On consumer pages the global filter API returns values from
  // ALL consumers which are irrelevant. The additional-actions columns are
  // alert and reading fields that route answers 400 for. Skip the fetch on
  // both and use row options.
  const isConsumerPage = /\/consumers\/[^/]+|\/addntion-actions/.test(
    window.location.pathname
  )

  // Fetch options from API when component mounts (skip on consumer pages)
  useEffect(() => {
    // The weekday value is derived from the row timestamp, so it must use
    // the values already present in the table rather than API option data.
    if (!field || customOptions || rowsOnly || field === 'dayOfWeek' || isConsumerPage) return

    const fetchOptions = async () => {
      setIsLoading(true)
      const accountId = localStorage.getItem('accountId')
      if (!accountId) {
        setIsLoading(false)
        return
      }

      const options = await getFilterOptions(accountId, field, langKey)
      setApiOptions(options)
      setIsLoading(false)
    }

    fetchOptions()
  }, [field, customOptions, rowsOnly, isConsumerPage, langKey])

  // Map boolean raw values to user-friendly labels
  const booleanLabel = useCallback(
    (val: string): string => {
      const lower = val.toLowerCase()
      if (lower === 'true' || lower === 't')
        return langKey === 'he' ? 'כן' : 'Yes'
      if (lower === 'false' || lower === 'f')
        return langKey === 'he' ? 'לא' : 'No'
      return val
    },
    [langKey]
  )

  // Grid-filter mode only (a `field` is what wires this to the table store):
  // an active filter collapses the list to the selected options. Single-select
  // node controls (NodeSelect) pass custom options WITHOUT a field and always
  // have a selection (e.g. severity defaults to '0'), so collapsing would hide
  // every alternative and make the control impossible to change.
  const hasActiveFilter = !!field && checked.length > 0

  // Every option on offer, before the search text narrows the list
  const offeredOptions = useMemo(() => {
    // Check if API options look like boolean values
    const isBooleanOptions =
      apiOptions.length > 0 &&
      apiOptions.every(opt =>
        ['true', 'false', 't', 'f'].includes(opt.toLowerCase())
      )

    // On consumer pages, only use row-extracted options. On other pages,
    // prefer row options but fall back to API options when no rows exist.
    let contextOptions = isConsumerPage ? rowOptions : (rowOptions.length > 0 ? rowOptions : apiOptions)
    // A server grid answers a pick with the rows of the picked values only, so the rows no
    // longer list the other values. The options seen before the first pick stay on offer.
    const remembered = hasActiveFilter && field ? searchFilterOptions[field] : undefined
    if (remembered) contextOptions = Array.from(new Set([...remembered, ...contextOptions])).sort()
    if (field === 'dayOfWeek' || field === 'day') {
      contextOptions = WEEKDAYS_OPTIONS
    }

    // Use custom options if provided, otherwise use context-aware options
    const baseOptions = customOptions
      ? customOptions.map(opt => ({ id: String(opt.id), label: opt.label }))
      : contextOptions.map(opt => ({
          id: opt,
          label: isBooleanOptions
            ? booleanLabel(opt)
            : formatFilterLabel(opt, field || ''),
        }))

    // Keep selected values visible even when applying the filter temporarily
    // makes the table/API response empty.
    const knownIds = new Set(baseOptions.map(option => option.id))
    const optionsWithSelected = [
      ...baseOptions,
      ...checked
        .filter(id => !knownIds.has(id))
        .map(id => ({ id, label: formatFilterLabel(id, field || '') })),
    ]

    return optionsWithSelected
  }, [
    customOptions,
    apiOptions,
    rowOptions,
    booleanLabel,
    hasActiveFilter,
    checked,
    field,
    searchFilterOptions,
  ])

  const displayOptions = useMemo(() => {
    if (!searchValue.trim()) return offeredOptions
    return offeredOptions.filter(opt =>
      opt.label.toLowerCase().includes(searchValue.toLowerCase())
    )
  }, [offeredOptions, searchValue])

  const handleToggle = (optionId: string) => {
    const newChecked = checked.includes(optionId)
      ? checked.filter(id => id !== optionId)
      : [...checked, optionId]

    setChecked(newChecked)
    onChange?.(newChecked)

    if (field) {
      const seen = hasActiveFilter ? (searchFilterOptions[field] ?? []) : []
      const offered = offeredOptions.map(option => option.id)
      rememberSearchFilterOptions(field, Array.from(new Set([...seen, ...offered])))
      // Update store to trigger API refetch
      updateFieldFilter(field, newChecked)
    }
  }

  const handleClear = useCallback(() => {
    setChecked([])
    setSearchValue('')
    onChange?.([])
    if (field) {
      updateFieldFilter(field, [])
    }
  }, [field, onChange, updateFieldFilter])

  return (
    <Box
      sx={{
        padding: bare ? 0 : 1,
        backgroundColor: bare ? 'transparent' : colors.bg,
        width: bare ? '100%' : 'max-content',
        minWidth: 115,
        maxWidth: bare ? 'none' : 240,
        height: 'auto',
        maxHeight: bare ? '240px' : '300px',
        overflowY: 'auto',

        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: colors.scrollTrack,
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: colors.scrollThumb,
          borderRadius: '4px',
          transition: 'background 0.3s ease',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: colors.scrollThumbHover,
        },
      }}
    >
      <SearchBar
        placeholder="חיפוש..."
        width="100%"
        height={30}
        onSearch={setSearchValue}
      />

      {hasActiveFilter && (
        <Typography
          sx={{
            color: colors.text,
            fontFamily: 'Rubik, sans-serif',
            fontSize: 13,
            fontWeight: 500,
            textAlign: 'center',
            py: 0.5,
          }}
        >
          {checked.length === 1 ? 'נבחר ערך אחד' : `נבחרו ${checked.length} ערכים`}
        </Typography>
      )}

      {isLoading && !hasActiveFilter ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : displayOptions.length === 0 ? (
        <Typography
          sx={{
            color: colors.text,
            textAlign: 'center',
            py: 2,
            fontSize: 14,
          }}
        >
          לא נמצאו אפשרויות
        </Typography>
      ) : (
        displayOptions.map(option => (
          <Box
            key={option.id}
            onClick={() => handleToggle(option.id)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              paddingTop: 1,
              paddingBottom: 1,
              paddingRight: 0.75,
              paddingLeft: 0.75,
              cursor: 'pointer',
              backgroundColor: checked.includes(option.id)
                ? colors.selectedBg
                : 'transparent',
              '&:hover': {
                backgroundColor: colors.hoverBg,
                border: `1px solid ${colors.hoverBorder}`,
                borderRadius: '4.5px',
              },
            }}
          >
            <Typography
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 1,
                color: colors.text,
                fontFamily: 'Rubik, sans-serif',
                fontStyle: 'light',
                fontSize: 14,
                fontWeight: checked.includes(option.id) ? 'bold' : '400',
              }}
            >
              {option.label}
              {Icon && <Icon />}
            </Typography>
            {checkBox && (
              <Checkbox
                size="small"
                sx={{
                  color: '#E4E7EB',
                  '&.Mui-checked': {
                    color: '#81B5FF',
                  },
                }}
                checked={checked.includes(option.id)}
                onClick={event => event.stopPropagation()}
                onChange={() => handleToggle(option.id)}
              />
            )}
          </Box>
        ))
      )}

      {!bare && checked.length > 0 && (
        <Typography
          onClick={handleClear}
          sx={{
            cursor: 'pointer',
            color: '#E53E3E',
            fontSize: 13,
            fontWeight: 500,
            mt: '8px',
            textAlign: 'center',
            fontFamily: 'Rubik, sans-serif',
            borderTop: `1px solid ${colors.hoverBorder}`,
            pt: 1,
            '&:hover': { color: '#C53030' },
          }}
        >
          נקה סינון
        </Typography>
      )}
    </Box>
  )
}

export default OptionsFilter
